import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { HiOutlineArrowUpTray } from "react-icons/hi2";

import type { DocumentWorkflowFile } from "../api/generated";
import { isApiClientError, isApiResponseValidationError } from "../api/errors";
import { signingAvailability } from "../entities/document-workflow";
import { uploadConfirmedFile } from "../entities/files";
import { DocumentFileRow } from "./DocumentFileRow";
import {
  DocumentFilesContext,
  type DocumentFilesContextValue,
  type SignatureCapabilitiesState,
} from "./documentFilesContext";
import { fileSourceView, pluralize, type FileSourceKind } from "./fileLabels";
import { renameMessage } from "./filesSupport";
import { FileRenameDialog } from "./FolderDialogs";
import { SourceFileCard } from "./SourceFileCard";
import { SectionSwitcher } from "./SectionSwitcher";
import type { ShellServices } from "./services";
import { unavailableReasons } from "./signingMessages";
import {
  useDocumentFileSources,
  type DiscussionFile,
  type SourceFile,
} from "./useDocumentFileSources";
import { Notice } from "./Notice";

/**
 * Причина, по которой список вложений не показан.
 *
 * Раньше здесь на любую неудачу писалось «нет права на файлы документа». Это
 * сбивало с толку: чаще всего дело было не в правах, а человек шёл выяснять
 * доступы вместо настоящей причины.
 */
function attachmentsError(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.status === 403)
      return "Вложения недоступны: нет права на файлы документа.";
    if (error.status === 404)
      return "Документ не найден или больше не доступен.";
    if (error.status === 503)
      return "Хранилище временно недоступно. Повторите попытку позже.";
  }
  if (isApiResponseValidationError(error)) {
    return "Список вложений пришёл в неожидаемом виде — сообщите об этом в поддержку.";
  }

  return "Не удалось загрузить список вложений. Повторите попытку.";
}

function uploadError(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.status === 403)
      return "Недостаточно прав для загрузки файлов к документу.";
    if (error.status === 413) return "Файл больше, чем принимает хранилище.";
    if (error.status === 422)
      return "Не удалось прикрепить файл к документу. Повторите попытку.";
    if (error.status === 503)
      return "Хранилище временно недоступно. Повторите попытку позже.";
  }
  return "Не удалось загрузить и прикрепить файл. Проверьте подключение и повторите попытку.";
}

function deleteError(error: unknown): string {
  if (
    isApiClientError(error) &&
    error.code === "document_workflow.immutable_version"
  ) {
    return "Файл с подписанной версией удалить нельзя: подписанные версии неизменяемы.";
  }

  return "Не удалось удалить файл. Обновите карточку и повторите попытку.";
}

type Filter = "all" | Exclude<FileSourceKind, "other">;

const filterLabels: Record<Filter, string> = {
  all: "Все",
  manual: "Загруженные",
  email_attachment: "Из переписки",
  comment_attachment: "Из обсуждения",
  material: "Материалы",
};

type Entry =
  | { readonly kind: "file"; readonly file: DocumentWorkflowFile }
  | { readonly kind: "source"; readonly source: SourceFile };

function entryFilter(entry: Entry): Filter {
  const origin =
    entry.kind === "file"
      ? fileSourceView(entry.file.provenance.type).kind
      : entry.source.origin;

  if (origin === "email_attachment") {
    const messageId =
      entry.kind === "file"
        ? entry.file.provenance.reference?.mail_message_id
        : entry.source.messageId;
    // Тип без сохранённой ссылки не подтверждает принадлежность к письму.
    return typeof messageId === "string" && messageId !== ""
      ? "email_attachment"
      : "manual";
  }
  return origin === "other" ? "manual" : origin;
}

/**
 * Файлы документа: логические файлы, их версии, происхождение и подписи, а
 * рядом — файлы из переписки и обсуждения, которые можно добавить в файлы
 * документа и подписать.
 *
 * Список запрашивается отдельно от самого документа: у файлов своё право на
 * просмотр, и карточка обязана открываться даже тому, кому файлы не показывают.
 * Права (`canUpload`, `canUnlink`, `canSign`) — подсказки интерфейсу: границей
 * остаются проверки сервера.
 */
export function DocumentAttachments({
  documentId,
  accessScopeId,
  canUpload = false,
  canUnlink = false,
  canSign = false,
  discussionFiles = [],
  discussionFilesPending = false,
  discussionFilesError = false,
  canViewDiscussion = false,
  authorName = () => "Сотрудник",
  onShowInMail,
  onShowInDiscussion,
  services,
}: {
  documentId: string;
  accessScopeId?: string;
  canUpload?: boolean;
  canUnlink?: boolean;
  canSign?: boolean;
  /** Вложения сообщений внутреннего обсуждения. */
  discussionFiles?: readonly DiscussionFile[];
  discussionFilesPending?: boolean;
  discussionFilesError?: boolean;
  canViewDiscussion?: boolean;
  /** Имя сотрудника по идентификатору автора или связавшего. */
  authorName?: (id: string | null) => string;
  /** Показать письмо, из которого пришёл файл. */
  onShowInMail?: (messageId: string) => void;
  /** Показать сообщение обсуждения, из которого пришёл файл. */
  onShowInDiscussion?: (commentId: string) => void;
  services: ShellServices;
}) {
  const client = useQueryClient();
  const [deleteCandidate, setDeleteCandidate] =
    useState<DocumentWorkflowFile | null>(null);
  const [renameCandidate, setRenameCandidate] =
    useState<DocumentWorkflowFile | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [autoSignVersionId, setAutoSignVersionId] = useState<string | null>(
    null,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const noticeId = useId();
  const files = useQuery({
    queryKey: ["document-workflow", "documents", documentId, "files"],
    queryFn: () => services.documentWorkflow.documentFiles(documentId),
    retry: false,
  });
  const items = useMemo(() => files.data ?? [], [files.data]);

  const sourceFiles = useDocumentFileSources({
    services,
    documentId,
    discussionFiles: canViewDiscussion ? discussionFiles : [],
    enabled: true,
  });
  // Уже занесённые в файлы документа источники повторно не показываются.
  const registeredBlobs = useMemo(
    () =>
      new Set(
        items.flatMap((file) =>
          file.current_version?.file_id ? [file.current_version.file_id] : [],
        ),
      ),
    [items],
  );
  const pendingSources = useMemo(
    () =>
      sourceFiles.sources.filter(
        (source) => !registeredBlobs.has(source.blobId),
      ),
    [sourceFiles.sources, registeredBlobs],
  );

  // Возможности подписи спрашиваются до любого действия подписания: пока
  // сервер не ответил, кнопка подписи недоступна и плагин не трогается.
  const capabilities = useQuery({
    queryKey: ["document-workflow", "signature-capabilities"],
    queryFn: () => services.documentWorkflow.signatureCapabilities(),
    enabled: items.length + pendingSources.length > 0,
    retry: false,
  });
  const retryCapabilities = capabilities.refetch;
  const signatureCapabilities = useMemo<SignatureCapabilitiesState>(
    () =>
      capabilities.isError
        ? { state: "error", retry: () => void retryCapabilities() }
        : capabilities.data
          ? { state: "ready", data: capabilities.data }
          : { state: "pending" },
    [capabilities.data, capabilities.isError, retryCapabilities],
  );

  const upload = useMutation({
    // Тот же путь, что у новой версии: подписанная ссылка → хранилище →
    // подтверждение; к документу уходит только идентификатор файла.
    mutationFn: async (file: File) => {
      const confirmed = await uploadConfirmedFile(services.files, file, {
        accessScopeId,
        onProgress: setProgress,
      });
      await services.documentWorkflow.attachFile(documentId, {
        confirmed_file_id: confirmed.id,
        display_name: file.name,
      });
    },
    onSuccess: () => void files.refetch(),
    onSettled: () => setProgress(null),
  });
  const remove = useMutation({
    mutationFn: (file: DocumentWorkflowFile) =>
      services.documentWorkflow.deleteDocumentFile(file.id),
    onSuccess: () => {
      setDeleteCandidate(null);
      void files.refetch();
    },
  });
  const rename = useMutation({
    mutationFn: ({
      file,
      filename,
    }: {
      file: DocumentWorkflowFile;
      filename: string;
    }) => services.documentWorkflow.renameDocumentFile(file.id, filename),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["document-workflow"] });
      setRenameCandidate(null);
    },
  });

  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const [file] = event.target.files ?? [];
    event.target.value = "";
    if (file) upload.mutate(file);
  };
  const requestSigning = useCallback(
    (versionId: string) => setAutoSignVersionId(versionId),
    [],
  );

  const context = useMemo<DocumentFilesContextValue>(
    () => ({
      services,
      documentId,
      accessScopeId,
      canUpload,
      canUnlink,
      canSign,
      canViewDiscussion,
      authorName,
      onShowInMail,
      onShowInDiscussion,
      signatureCapabilities,
      signingNoticeId: noticeId,
      autoSignVersionId,
      requestSigning,
    }),
    [
      services,
      documentId,
      accessScopeId,
      canUpload,
      canUnlink,
      canSign,
      canViewDiscussion,
      authorName,
      onShowInMail,
      onShowInDiscussion,
      signatureCapabilities,
      noticeId,
      autoSignVersionId,
      requestSigning,
    ],
  );

  const entries = useMemo<readonly Entry[]>(
    () => [
      ...items.map((file): Entry => ({ kind: "file", file })),
      ...pendingSources.map((source): Entry => ({ kind: "source", source })),
    ],
    [items, pendingSources],
  );
  const counts = new Map<Filter, number>();
  for (const entry of entries) {
    const key = entryFilter(entry);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const filterKeys = (Object.keys(filterLabels) as Filter[]).filter(
    (key) =>
      key !== "all" && key !== "email_attachment" && (counts.get(key) ?? 0) > 0,
  );
  const active: Filter =
    filter === "all" ||
    filter === "email_attachment" ||
    filterKeys.includes(filter)
      ? filter
      : "all";
  const mailCount = counts.get("email_attachment") ?? 0;
  const internalCount = entries.length - mailCount;
  const visible = entries.filter((entry) =>
    active === "all"
      ? entryFilter(entry) !== "email_attachment"
      : entryFilter(entry) === active,
  );

  const signedCount = items.filter(
    (file) => file.current_version?.signature_state === "signed",
  ).length;
  const quarantinedCount = entries.filter(
    (entry) =>
      (entry.kind === "file"
        ? entry.file.scan_status
        : entry.source.scanStatus) === "quarantined",
  ).length;
  const checkingCount = entries.filter(
    (entry) =>
      (entry.kind === "file"
        ? entry.file.scan_status
        : entry.source.scanStatus) === "pending",
  ).length;

  // Общая причина недоступности подписи: одна на весь список, а не на каждый файл.
  const globalAvailability = signingAvailability({
    capabilities: signatureCapabilities,
    canSign,
    browserProfiles: services.cryptoPro?.supportedProfiles ?? [],
    version: { scan_status: "clean", sha256: "-" },
  });
  const capabilitiesData =
    signatureCapabilities.state === "ready" ? signatureCapabilities.data : null;
  const degraded =
    capabilitiesData?.enabled === true &&
    !capabilitiesData.server_verifier_available;
  const globalReason =
    globalAvailability.status === "unavailable" &&
    globalAvailability.reason !== "version_not_ready"
      ? unavailableReasons[globalAvailability.reason]
      : null;
  const empty =
    !files.isPending &&
    !files.isError &&
    items.length === 0 &&
    pendingSources.length === 0 &&
    !sourceFiles.pending;

  return (
    <DocumentFilesContext.Provider value={context}>
      <section
        className="dw-preview__attachments files-panel"
        aria-labelledby="dw-attachments-title"
      >
        <div className="files-head">
          <div className="files-head__text">
            <h3 id="dw-attachments-title">Файлы документа</h3>
            {entries.length ? (
              <p className="files-summary">
                {`${entries.length} ${pluralize(entries.length, ["файл", "файла", "файлов"])}`}
                {signedCount
                  ? ` · подписано: ${signedCount}`
                  : " · подписанных нет"}
                {quarantinedCount ? ` · в карантине: ${quarantinedCount}` : ""}
                {checkingCount ? ` · на проверке: ${checkingCount}` : ""}
              </p>
            ) : null}
          </div>
          {canUpload ? (
            <div className="files-head__action">
              <button
                className="button button--primary button--compact"
                type="button"
                disabled={upload.isPending}
                onClick={() => inputRef.current?.click()}
              >
                <HiOutlineArrowUpTray aria-hidden="true" />
                <span>
                  {upload.isPending
                    ? progress !== null && progress < 1
                      ? `Загружаем… ${Math.round(progress * 100)}%`
                      : "Загружаем и прикрепляем…"
                    : "Загрузить файл"}
                </span>
              </button>
              <input
                ref={inputRef}
                className="visually-hidden"
                aria-label="Загрузить файл к документу"
                type="file"
                onChange={selectFile}
              />
            </div>
          ) : null}
        </div>
        <SectionSwitcher<Filter>
          items={[
            {
              id: "all",
              label: "Для внутреннего пользования",
              hint: `${internalCount} ${pluralize(internalCount, ["файл", "файла", "файлов"])}`,
            },
            {
              id: "email_attachment",
              label: "В составе письма",
              hint: `${mailCount} ${pluralize(mailCount, ["файл", "файла", "файлов"])}`,
            },
          ]}
          value={active === "email_attachment" ? "email_attachment" : "all"}
          onChange={setFilter}
          ariaLabel="Категории файлов документа"
        />
        {upload.isError ? <Notice>{uploadError(upload.error)}</Notice> : null}
        {globalReason || degraded ? (
          <div id={noticeId} className="files-notice" role="note">
            {globalReason ? (
              <p>
                <strong>Подпись сейчас недоступна.</strong> {globalReason}
                {signatureCapabilities.state === "error" ? (
                  <>
                    {" "}
                    <button
                      className="button button--text button--compact"
                      type="button"
                      onClick={signatureCapabilities.retry}
                    >
                      Повторить запрос
                    </button>
                  </>
                ) : null}
              </p>
            ) : null}
            {degraded ? (
              <p>
                <strong>Серверная проверка сейчас недоступна.</strong> Подпись
                можно создать в браузере, но сервер не сохранит её, пока
                проверка не заработает.
              </p>
            ) : null}
          </div>
        ) : null}
        {active !== "email_attachment" && filterKeys.length > 1 ? (
          <div
            className="files-filter"
            role="group"
            aria-label="Источник файлов"
          >
            {(["all", ...filterKeys] as Filter[]).map((key) => (
              <button
                key={key}
                type="button"
                className="files-filter__chip"
                aria-pressed={active === key}
                onClick={() => setFilter(key)}
              >
                {filterLabels[key]}
                <span>{key === "all" ? internalCount : counts.get(key)}</span>
              </button>
            ))}
          </div>
        ) : null}
        {files.isPending ? <p aria-busy="true">Загружаем вложения…</p> : null}
        {files.isError ? (
          <p className="dw-secondary">{attachmentsError(files.error)}</p>
        ) : null}
        {empty ? (
          <p className="dw-secondary">К документу ничего не приложено.</p>
        ) : null}
        {!empty &&
        !files.isPending &&
        !files.isError &&
        !sourceFiles.pending &&
        !discussionFilesPending &&
        visible.length === 0 ? (
          <p className="dw-secondary">В выбранной категории файлов нет.</p>
        ) : null}
        {visible.length ? (
          <ul className="file-list">
            {visible.map((entry) =>
              entry.kind === "file" ? (
                <DocumentFileRow
                  key={entry.file.id}
                  file={entry.file}
                  deleting={remove.isPending}
                  onDelete={setDeleteCandidate}
                  onRename={(file) => {
                    rename.reset();
                    setRenameCandidate(file);
                  }}
                />
              ) : (
                <SourceFileCard key={entry.source.key} source={entry.source} />
              ),
            )}
          </ul>
        ) : null}
        {sourceFiles.pending || discussionFilesPending ? (
          <p className="dw-secondary" aria-busy="true">
            Ищем файлы в переписке и обсуждении…
          </p>
        ) : null}
        {sourceFiles.mailFailed ||
        sourceFiles.discussionFailed ||
        discussionFilesError ? (
          <p className="dw-secondary">
            Не удалось получить файлы из{" "}
            {[
              sourceFiles.mailFailed ? "переписки" : null,
              sourceFiles.discussionFailed || discussionFilesError
                ? "обсуждения"
                : null,
            ]
              .filter(Boolean)
              .join(" и ")}
            . Остальные файлы показаны.{" "}
            <button
              className="button button--text button--compact"
              type="button"
              onClick={sourceFiles.retry}
            >
              Повторить
            </button>
          </p>
        ) : null}
        {remove.isError ? <Notice>{deleteError(remove.error)}</Notice> : null}
        {renameCandidate ? (
          <FileRenameDialog
            filename={renameCandidate.display_name}
            pending={rename.isPending}
            error={rename.isError ? renameMessage(rename.error) : null}
            onSubmit={(filename) =>
              rename.mutate({ file: renameCandidate, filename })
            }
            onClose={() => {
              if (!rename.isPending) setRenameCandidate(null);
            }}
          />
        ) : null}
        {deleteCandidate ? (
          <div
            className="file-confirm"
            role="alertdialog"
            aria-labelledby="attachment-delete-title"
          >
            <p id="attachment-delete-title">
              Удалить «{deleteCandidate.display_name}» из документа?
            </p>
            <p className="file-hint">
              Удаляются все версии этого файла в документе. Сам файл в хранилище
              остаётся, пока он используется в других местах — письме,
              обсуждении, других документах.
            </p>
            <div className="file-actions">
              <button
                className="button button--danger button--compact"
                type="button"
                disabled={remove.isPending}
                onClick={() => remove.mutate(deleteCandidate)}
              >
                {remove.isPending ? "Удаляем…" : "Удалить файл"}
              </button>
              <button
                className="button button--text button--compact"
                type="button"
                disabled={remove.isPending}
                onClick={() => setDeleteCandidate(null)}
              >
                Отмена
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </DocumentFilesContext.Provider>
  );
}
